import React from 'react';

import { getStaticsBaseUrl } from '@oysterjs/core/config';

import type { PageFurniture } from '~/components/document-editor/extensions/page-furniture';
import { fetchWithRenderGrant } from '~/components/workspaces/lib/render-grant';

/** The margin and running header and footer the renderer will print around this document. */
export function usePageFurniture(params: {
  enabled: boolean;
  workspaceId?: string;
  artifactId?: string;
  artifactVersion?: number;
  artifactRevision?: string | number;
  templateId?: string;
}) {
  const { enabled, workspaceId, artifactId, artifactVersion, artifactRevision, templateId } =
    params;
  const [furniture, setFurniture] = React.useState<PageFurniture | null>(null);

  React.useEffect(() => {
    if (!enabled || !workspaceId || !artifactId || artifactVersion === undefined) {
      setFurniture(null);
      return;
    }

    const controller = new AbortController();
    const query = new URLSearchParams({ version: String(artifactVersion) });
    // Older statics reads the query style during mixed deployments.
    if (templateId) query.set('style_id', templateId);
    if (artifactRevision !== undefined) query.set('render_revision', String(artifactRevision));

    const url = `${getStaticsBaseUrl()}/agency/html/${workspaceId}/${artifactId}/page-furniture?${query.toString()}`;
    fetchWithRenderGrant(
      url,
      { workspaceId, artifactId, version: artifactVersion, styleId: templateId },
      controller.signal
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (!controller.signal.aborted) {
          setFurniture(body && body.margins ? (body as PageFurniture) : null);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setFurniture(null);
      });

    return () => controller.abort();
  }, [enabled, workspaceId, artifactId, artifactVersion, artifactRevision, templateId]);

  return furniture;
}
